/* eslint-disable @typescript-eslint/no-explicit-any */
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { Box, CircularProgress, SxProps } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageGallery, { ReactImageGalleryItem } from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import siteImages from "../../data/siteImages";
import i18n from "../../i18n";
import { colors } from "../../theme/colors";
import { useIsMobile } from "../../utils/useIsMobile";
import useKeyPress from "../../utils/useKeyPress";
import Button, { ButtonVariant } from "./Button";

const GalleryImage = ({
  item,
  isFullScreen
}: {
  item: ReactImageGalleryItem;
  isFullScreen: boolean;
}) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <Box className="image-gallery-image">
      {!loaded && (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16/9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.2)"
          }}>
          {!isFullScreen && (
            <Box
              sx={(theme) => ({
                position: "absolute",
                inset: 0,
                background: theme.palette.background.default,
                zIndex: -10
              })}
            />
          )}
          <CircularProgress
            size={40}
            sx={(theme) => ({
              color: theme.palette.mode === "light" ? colors.black : colors.white
            })}
          />
        </Box>
      )}
      <img
        src={item.original}
        alt={item.originalAlt ?? item.originalTitle ?? ""}
        onLoad={() => setLoaded(true)}
        style={{ display: loaded ? "block" : "none", width: "100%" }}
      />
    </Box>
  );
};

const handleFullScreenChange = (isFullscreen: boolean) => {
  const element = document.getElementsByClassName("image-gallery-swipe")[0];

  if (!element) {
    return;
  }

  if (isFullscreen) {
    element.classList.add("image-gallery-fullscreen-swipe");
  } else {
    element.classList.remove("image-gallery-fullscreen-swipe");
  }
};

const galleryControlsStyle: SxProps = {
  position: "absolute",
  zIndex: 4,
  boxShadow: "0px 0px 15px 7px rgba(0, 0, 0, 0.25)"
};

type ButtonProps = {
  disabled?: boolean;
  onClick: any;
  onManualNav?: () => void;
  label: string;
};

const LeftNav = ({ disabled, onClick, onManualNav, label }: ButtonProps) => {
  useKeyPress(["ArrowLeft"], () => {
    onManualNav?.();
    onClick();
  });

  return (
    <Button
      variant={ButtonVariant.accent}
      startIcon={<ArrowBackOutlinedIcon titleAccess={label} />}
      disabled={disabled}
      onClick={() => {
        onManualNav?.();
        onClick();
      }}
      sx={{
        top: "35%",
        left: 0,
        margin: 5,
        display: { xs: "none", md: "flex" },
        ...galleryControlsStyle
      }}
    />
  );
};

const RightNav = ({ disabled, onClick, onManualNav, label }: ButtonProps) => {
  useKeyPress(["ArrowRight"], () => {
    onManualNav?.();
    onClick();
  });

  return (
    <Button
      variant={ButtonVariant.accent}
      startIcon={<ArrowForwardOutlinedIcon titleAccess={label} />}
      disabled={disabled}
      onClick={() => {
        onManualNav?.();
        onClick();
      }}
      sx={{
        top: "35%",
        right: 0,
        margin: 5,
        display: { xs: "none", md: "flex" },
        ...galleryControlsStyle
      }}
    />
  );
};

const Play = ({ isPlaying, onClick, disabled, label }: { isPlaying: boolean } & ButtonProps) => {
  useKeyPress([" "], () => {
    onClick();
  });

  return (
    <Button
      variant={ButtonVariant.accent}
      startIcon={
        isPlaying ? (
          <PauseOutlinedIcon titleAccess={label} />
        ) : (
          <PlayArrowOutlinedIcon titleAccess={label} />
        )
      }
      onClick={onClick}
      disabled={disabled}
      sx={{
        bottom: 0,
        left: 0,
        margin: 5,
        display: { xs: "none", md: "flex" },
        ...galleryControlsStyle
      }}
    />
  );
};

const FullScreen = ({
  isFullscreen,
  onClick,
  disabled,
  label,
  setFullScreen
}: {
  isFullscreen: boolean;
  setFullScreen: (f: boolean) => void;
} & ButtonProps) => (
  <Button
    variant={ButtonVariant.accent}
    startIcon={
      isFullscreen ? (
        <FullscreenExitOutlinedIcon titleAccess={label} />
      ) : (
        <FullscreenOutlinedIcon titleAccess={label} />
      )
    }
    onClick={(event) => {
      handleFullScreenChange(!isFullscreen);
      setFullScreen(!isFullscreen);
      onClick(event);
    }}
    disabled={disabled}
    sx={{
      bottom: isFullscreen ? "1.5rem" : 0,
      right: 0,
      margin: 5,
      ...galleryControlsStyle
    }}
  />
);

const ImagePreview = ({
  images,
  autoplay,
  showFullscreenButton = true,
  maxHeight = "700px",
  maxWidth = "100%",
  sx = {}
}: {
  images: Array<ReactImageGalleryItem>;
  autoplay?: boolean;
  showFullscreenButton?: boolean;
  maxHeight?: string;
  maxWidth?: string;
  sx?: SxProps;
}) => {
  const { t } = useTranslation();

  const [fullScreen, setFullScreen] = useState(false);
  const [autoPlayStopped, setAutoPlayStopped] = useState(false);
  const galleryRef = useRef<ImageGallery>(null);
  const isMobile = useIsMobile();

  // Error image is language-specific
  const lang = i18n.language;
  const errorImageSrc =
    (siteImages.imageError as Record<string, { src: string; alt: string }>)[lang]?.src ??
    siteImages.imageError["en"]?.src;

  const stopAutoPlay = useCallback(() => {
    if (!autoplay) return;
    galleryRef.current?.pause();
    setAutoPlayStopped(true);
  }, [autoplay]);

  const exitHandler = () => {
    const doc = document as any;
    if (!doc.webkitIsFullScreen && !doc.mozFullScreen && !doc.msFullscreenElement) {
      handleFullScreenChange(false);
      setFullScreen(false);
    }
  };

  useEffect(() => {
    if (document.addEventListener) {
      document.addEventListener("fullscreenchange", exitHandler, false);
      document.addEventListener("mozfullscreenchange", exitHandler, false);
      document.addEventListener("MSFullscreenChange", exitHandler, false);
      document.addEventListener("webkitfullscreenchange", exitHandler, false);
    }
  }, []);

  return images?.length > 0 ? (
    <Box
      sx={{
        width: fullScreen ? "100%" : `${maxWidth} !important`,
        mx: "auto",
        "& img": {
          objectFit: !fullScreen ? "cover !important" : "contain !important",
          maxHeight: fullScreen ? "100vh" : `${maxHeight} !important`,
          aspectRatio: fullScreen ? "unset" : "16/9 !important",
          background: "transparent !important"
        },
        ...sx
      }}>
      <ImageGallery
        lazyLoad={true}
        items={images}
        onErrorImageURL={errorImageSrc}
        renderLeftNav={(onClick, disabled) => (
          <LeftNav
            label={t("buttons.previous")}
            onClick={onClick}
            disabled={disabled}
            onManualNav={stopAutoPlay}
          />
        )}
        renderRightNav={(onClick, disabled) => (
          <RightNav
            label={t("buttons.next")}
            onClick={onClick}
            disabled={disabled}
            onManualNav={stopAutoPlay}
          />
        )}
        renderPlayPauseButton={(onClick, isPlaying) => {
          if (isPlaying && autoPlayStopped) {
            setAutoPlayStopped(false);
          }
          return (
            <Play
              label={t(`buttons.${isPlaying ? "pause" : "play"}`)}
              onClick={onClick}
              isPlaying={isPlaying}
            />
          );
        }}
        renderFullscreenButton={(onClick, isFullscreen) => (
          <FullScreen
            label={t(`buttons.${isFullscreen ? "exit" : "fullscreen"}`)}
            onClick={onClick}
            isFullscreen={isFullscreen}
            setFullScreen={setFullScreen}
          />
        )}
        renderItem={(item) => <GalleryImage item={item} isFullScreen={fullScreen} />}
        showFullscreenButton={showFullscreenButton}
        showPlayButton={autoPlayStopped}
        additionalClass="w-full"
        showThumbnails={false}
        showBullets={(fullScreen || !isMobile) && images.length > 1}
        onTouchEnd={stopAutoPlay}
        onClick={stopAutoPlay}
        ref={galleryRef}
        autoPlay={autoplay}
        useBrowserFullscreen={true}
      />
    </Box>
  ) : (
    <Box
      sx={(theme) => ({
        width: "100%",
        aspectRatio: "2/1",
        overflow: "hidden",
        background: "white",
        border: `2px solid ${colors.brown}`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        zIndex: 100,
        borderRadius: theme.spacing(4)
      })}>
      <ImageNotSupportedIcon
        titleAccess={t("buttons.imageNotAvailable")}
        sx={(theme) => ({
          fontSize: { xs: theme.spacing(5), md: theme.spacing(10) },
          color: colors.brown
        })}
      />
    </Box>
  );
};

export default ImagePreview;
